import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store/store'
import { clearSelectedEmployee } from '../../store/slices/admin/manualTimeOffSlice'
import { clearEmployeeScopedData } from '../../store/slices/Requestor/employeeInfoSlice'

/**
 * The Manual Time Off "acting on behalf of" employee selection is only
 * meaningful while the user is on the proxy pages (/admin/requestor, reached
 * exclusively via /admin/manual-timeoff). Previously the selection was only
 * cleared when the user clicked "Exit Requestor Proxy Manual Mode" — so
 * leaving those pages any other way (nav links, browser back, a bookmark)
 * left the selection in Redux/sessionStorage indefinitely, and every
 * employee-scoped section (requests, balances, employee info) kept showing
 * the selected employee's data instead of reverting to the logged-in user's.
 *
 * Mounted once near the root, inside the router, so it can watch every
 * navigation and clear the selection the moment the user leaves those pages.
 */
const ManualTimeOffContextGuard = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const hasSelection = useSelector(
    (state: RootState) => !!state.manualTimeOff.selectedEmployee
  )

  useEffect(() => {
    const matchesAdminRequestor = /^\/admin\/requestor(\/|$)/.test(location.pathname)
    const matchesManualTimeOff = /^\/admin\/manual-timeoff(\/|$)/.test(location.pathname)
    const isManualTimeOffProxyRoute = matchesAdminRequestor || matchesManualTimeOff

    if (!isManualTimeOffProxyRoute && hasSelection) {
      dispatch(clearSelectedEmployee())
      // Also clear the previously-fetched employee-scoped data itself (not
      // just the selection pointer) so a stray render can't show the exited
      // employee's data while the logged-in user's own fetch is in flight.
      dispatch(clearEmployeeScopedData())
      return
    }

    // The reverse case: the URL itself can land back on /admin/requestor
    // (e.g. the browser Back button restoring a history entry) after the
    // selection was already cleared — e.g. by this same guard, when the user
    // had navigated away in between. Without a selection there is no
    // employee left to act on behalf of, so the page would otherwise render
    // a half-broken "On behalf of Employee (Unknown)" state. Send the user
    // back to search/select an employee instead.
    if (matchesAdminRequestor && !hasSelection) {
      navigate('/admin/manual-timeoff', { replace: true })
    }
  }, [location.pathname, hasSelection, dispatch, navigate])

  return null
}

export default ManualTimeOffContextGuard
